export const parseCurrencyToNumber = (value: string): number | null => {
  if (!value || value === '.') return null
  const cleanValue = value.replace(/[^0-9.]/g, '')
  const parsed = parseFloat(cleanValue)
  return isNaN(parsed) ? null : parsed
}

export const formatCurrency = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return ''
  return value.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })
}

interface FormatCurrencyWithDollarSignOptions {
  numberOfDecimalsForM?: number
  numberOfDecimalsForK?: number
  showDollarSign?: boolean
}

export const formatCurrencyWithDollarSign = (
  value: number | null | undefined,
  {
    numberOfDecimalsForM = 0,
    numberOfDecimalsForK = 0,
    showDollarSign = true,
  }: FormatCurrencyWithDollarSignOptions = {},
): string => {
  if (value === null || value === undefined) return ''
  const currency = showDollarSign ? '$' : ''
  if (value >= 1000000) return `${currency}${(value / 1000000).toFixed(numberOfDecimalsForM)}M`
  if (value >= 1000) return `${currency}${(value / 1000).toFixed(numberOfDecimalsForK)}K`
  return `${currency}${value.toFixed(0)}`
}

// Enhanced parsing function for currency input fields
export const parseCurrencyInput = (input: string): number | null => {
  if (!input || input.trim() === '') return null

  // Remove all non-numeric characters except decimal point
  const cleanInput = input.replace(/[^0-9.]/g, '')

  // Handle multiple decimal points (keep only the first one)
  const parts = cleanInput.split('.')
  if (parts.length > 2) {
    const firstPart = parts[0]
    const rest = parts.slice(1).join('')
    const sanitizedInput = `${firstPart}.${rest}`
    const parsed = parseFloat(sanitizedInput)
    return isNaN(parsed) ? null : parsed
  }

  const parsed = parseFloat(cleanInput)
  return isNaN(parsed) ? null : parsed
}

/**
 * Formats a currency value to a compact string.
 * - If the value is greater than or equal to 1,000,000, it will be formatted as "X.XXM".
 * - If the value is greater than or equal to 1,000, it will be formatted as "X.XXK".
 * - Otherwise, it will be formatted as "X.XX".
 * @param value - The currency value to format.
 * @returns The formatted currency value.
 * @example
 * formatCurrencyCompact(1000000) // "1.00M"
 * formatCurrencyCompact(1000) // "1.00K"
 * formatCurrencyCompact(100) // "100"
 */
export const formatCurrencyCompact = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return ''

  if (value >= 1000000 && value % 10000 === 0) {
    const valueInMillions = value / 1000000
    const formattedMillions = valueInMillions.toFixed(2).replace(/\.?0+$/, '')
    return `$${formattedMillions}M`
  }

  if (value >= 1000 && value % 10 === 0) {
    const valueInThousands = value / 1000
    const formattedThousands = valueInThousands.toFixed(2).replace(/\.?0+$/, '')
    return `$${formattedThousands}K`
  }

  return `$${value.toLocaleString('en-US', { maximumFractionDigits: 2 })}`
}
